import { getAliquotaINPS } from '../../constants/aliquote';
import {
  getSogliePerAnno,
  calcolaRiepilogoFiscale,
  risolviVersamentiAnno,
  calcolaAnniAttivita,
  getAliquotaSostitutiva,
} from '../fiscalEngine';

export function buildUserContext({
  invoices,
  fiscalSettings,
  clients,
  user,
  selectedYear,
  getFiscalDataForYear,
}) {
  // Anno a cui si riferiscono riepilogo e calcoli: quello selezionato in app.
  // NB: non è necessariamente l'anno solare corrente.
  const annoRiferimento = selectedYear || new Date().getFullYear();

  // Fatture emesse nell'anno di riferimento
  const yearInvoices = (invoices || []).filter(
    (inv) => inv.issueDate && inv.issueDate.startsWith(String(annoRiferimento))
  );

  // Conteggio fatture pagate tra quelle EMESSE nell'anno (usato solo per il conteggio).
  const paidInvoices = yearInvoices.filter((inv) => inv.paid);
  // Incassato per cassa: fatture pagate con paymentDate nell'anno selezionato
  // (fallback a issueDate se pagata senza data di pagamento). Coerente con
  // InvoicesPage, DashboardPage e lo storico.
  const incassiAnno = (invoices || [])
    .filter((inv) => {
      if (!inv.paid) return false;
      const d = inv.paymentDate || inv.issueDate;
      return d && new Date(d).getFullYear() === annoRiferimento;
    })
    .reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);

  // Fatture non pagate
  const unpaidInvoices = yearInvoices.filter((inv) => !inv.paid);
  const totaleDaIncassare = unpaidInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);

  // Impostazioni fiscali
  const profitabilityCoefficient = fiscalSettings?.profitabilityCoefficient || 0.78;
  const inpsType = fiscalSettings?.inpsType || 'gestione-separata';
  const inpsRate = getAliquotaINPS(inpsType, annoRiferimento);
  const pivaYearStart = fiscalSettings?.pivaYearStart || annoRiferimento;

  // Anni di attività e aliquota dell'anno di riferimento, come in DashboardPage:
  // Olli deve dire gli stessi numeri che l'utente vede sulla dashboard.
  const anniAttivita = calcolaAnniAttivita(pivaYearStart, annoRiferimento);
  const aliquotaAutomatica = getAliquotaSostitutiva(pivaYearStart, annoRiferimento);

  // Calcoli fiscali: unica fonte è il motore (src/utils/fiscalEngine.js), come
  // in DashboardPage e FiscalSummaryCard. La deduzione INPS viene dai versamenti
  // dell'anno (fiscalByYear), non da fiscalSettings.contributiInpsPrecedenti.
  const fiscalData = getFiscalDataForYear ? getFiscalDataForYear(annoRiferimento) : {};
  const versamenti = risolviVersamentiAnno(fiscalData);
  const riepilogo = calcolaRiepilogoFiscale({
    incassi: incassiAnno,
    coefficiente: profitabilityCoefficient,
    aliquotaInps: inpsRate / 100,
    aliquotaImposta: aliquotaAutomatica / 100,
    fiscalData,
  });

  const imponibileLordo = riepilogo.imponibile;
  const contributiInps = riepilogo.inpsDovuto;
  const contributiDedotti = versamenti.contributiVersati;
  const imponibileNetto = Math.max(0, imponibileLordo - contributiDedotti);
  const imposta = riepilogo.tasseLorde;
  const totaleTasse = riepilogo.totaleDovuto;
  const nettoDisponibile = riepilogo.nettoDisponibile;

  // Anonimizzazione clienti con percentuali fatturato
  const clientMap = new Map();
  let clientIndex = 1;
  const clientTotals = {};

  yearInvoices.forEach((inv) => {
    const name = inv.clientName || 'Sconosciuto';
    if (!clientMap.has(name)) {
      clientMap.set(name, `Cliente ${clientIndex}`);
      clientIndex++;
    }
    const anonName = clientMap.get(name);
    clientTotals[anonName] = (clientTotals[anonName] || 0) + (inv.grossAmount || 0);
  });

  const totalefatturato = yearInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const clientiInfo = Object.entries(clientTotals)
    .sort(([, a], [, b]) => b - a)
    .map(([name, total]) => {
      const perc = totalefatturato > 0 ? ((total / totalefatturato) * 100).toFixed(1) : 0;
      return `${name}: ${perc}% del fatturato (${formatEuro(total)})`;
    });

  // Soglia forfettario: ragguagliata ai giorni di attività nel primo anno, piena dopo.
  const soglie = getSogliePerAnno(fiscalSettings, annoRiferimento);
  const fatturato85k = yearInvoices.reduce((sum, inv) => sum + (inv.grossAmount || 0), 0);
  const margine85k = soglie.soglia - fatturato85k;

  // Riepilogo storico su TUTTE le fatture (tutti gli anni), una voce per anno.
  // Dà a Olli visibilità sullo storico oltre l'anno selezionato.
  // - Fatturato/conteggio: per anno di EMISSIONE (issueDate, competenza).
  // - Incassato: per anno di PAGAMENTO (paymentDate, principio di cassa), come in
  //   InvoicesPage. Una fattura emessa nel 2025 e incassata nel 2026 → incassato 2026.
  const storicoMap = {};
  const ensureYear = (y) => {
    if (!storicoMap[y]) storicoMap[y] = { anno: y, totali: 0, fatturato: 0, incassato: 0 };
    return storicoMap[y];
  };
  (invoices || []).forEach((inv) => {
    const issueY = inv.issueDate ? inv.issueDate.slice(0, 4) : null;
    if (issueY) {
      const entry = ensureYear(issueY);
      entry.totali += 1;
      entry.fatturato += inv.grossAmount || 0;
    }
    if (inv.paid) {
      const payDate = inv.paymentDate || inv.issueDate;
      const payY = payDate ? payDate.slice(0, 4) : null;
      if (payY) ensureYear(payY).incassato += inv.grossAmount || 0;
    }
  });
  const storico = Object.values(storicoMap)
    .sort((a, b) => a.anno.localeCompare(b.anno))
    .map((s) => ({
      anno: s.anno,
      totali: s.totali,
      fatturato: formatEuro(s.fatturato),
      incassato: formatEuro(s.incassato),
    }));

  return {
    anno: annoRiferimento,
    anniAttivita,
    nomeUtente: user?.firstName || 'Utente',
    riepilogoFatture: {
      totali: yearInvoices.length,
      pagate: paidInvoices.length,
      nonPagate: unpaidInvoices.length,
      incassiAnno: formatEuro(incassiAnno),
      daIncassare: formatEuro(totaleDaIncassare),
    },
    calcoli: {
      coefficienteRedditività: profitabilityCoefficient,
      aliquotaImposta: `${aliquotaAutomatica}%`,
      aliquotaINPS: `${inpsRate}%`,
      tipoGestione: inpsType,
      contributiDedotti: formatEuro(contributiDedotti),
      imponibileLordo: formatEuro(imponibileLordo),
      contributiInps: formatEuro(contributiInps),
      imponibileNetto: formatEuro(imponibileNetto),
      imposta: formatEuro(imposta),
      totaleTasse: formatEuro(totaleTasse),
      versato: formatEuro(riepilogo.versatoTotale),
      residuo: formatEuro(riepilogo.residuo),
      nettoDisponibile: formatEuro(nettoDisponibile),
    },
    clienti: clientiInfo,
    soglia85k: {
      limite: formatEuro(soglie.soglia),
      limiteUscitaImmediata: formatEuro(soglie.sogliaExit),
      isRagguagliata: soglie.isRagguagliata,
      giorniAttivita: soglie.giorniAttivita,
      fatturatoCorrente: formatEuro(fatturato85k),
      margineResiduo: formatEuro(Math.max(0, margine85k)),
      percentualeUtilizzata:
        (soglie.soglia > 0 ? (fatturato85k / soglie.soglia) * 100 : 0).toFixed(1) + '%',
    },
    storico,
  };
}

function formatEuro(value) {
  const num = Number(value || 0);
  return num.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}
